import { CalendarBlank } from '@phosphor-icons/react';
import { useDateFormat } from '@eti/providers';
import { css, cx } from '@eti/styles';
import { date as dateUtil } from '@eti/utils';
import { breakpoints, Inline, vars } from '@etg/wings';
import { useMemo } from 'react';
import { BoundNew } from '@eti/types';
import TimelineWrapper from '../../../../common/TimelineWrapper';
import { useOrderDetails } from '../../../../../containers/OrderDetailsContext';
import SegmentGroup from './SegmentGroup';
import { getSegmentGroups } from './utils';

const boundHeadingStyles = css`
  font-size: 1rem;
  font-weight: 700;
`;

const wrapperStyles = css`
  border: 1px solid ${vars.colors.divider};
  border-radius: 3px;
  padding: 8px 16px;
`;

const inlineStyles = css`
  & > :first-child {
    padding: 8px 0;
    text-align: end;
    width: 55px;

    @media (min-width: ${breakpoints._768}) {
      width: 62px;
    }
  }

  & > :last-child {
    align-self: center;
    flex: 1;
    padding: 8px 0;
  }
`;

const locationHeadingStyles = css`
  word-break: break-word;
`;

const Bound = ({ bound }) => {
  const { day } = useDateFormat();
  const { customerDetails, flightInformation } = useOrderDetails();
  const firstSegment = bound.segments[0];
  const lastSegment = bound.segments[bound.segments.length - 1];
  const departureDate = dateUtil.format(firstSegment?.departuredAt, day);
  const origin = firstSegment?.origin.cityName;
  const destination = lastSegment?.destination.cityName;
  const dataTestIdPrefix = `self-checkin-${firstSegment?.origin.cityCode}-${lastSegment?.destination.cityCode}`;

  const segmentGroups = useMemo(
    () => getSegmentGroups(bound.segments, customerDetails, flightInformation),
    [bound.segments, customerDetails, flightInformation],
  );

  return (
    <div className={wrapperStyles} data-testid={dataTestIdPrefix}>
      <Inline className={inlineStyles} noWrap spacing={8}>
        <div className={boundHeadingStyles} data-testid={`${dataTestIdPrefix}-date`}>
          {departureDate}
        </div>
        <TimelineWrapper borderStyleAfter="dotted" iconSize={20}>
          <CalendarBlank
            aria-hidden="true"
            data-testid={`${dataTestIdPrefix}-calendar-icon`}
            size={20}
          />
        </TimelineWrapper>
        <div
          className={cx(boundHeadingStyles, locationHeadingStyles)}
          data-testid={`${dataTestIdPrefix}-origin-destination`}
        >
          {origin} - {destination}
        </div>
      </Inline>
      {segmentGroups.map((segmentGroup, index) => {
        const previousGroupArrival = segmentGroups[index - 1]?.arrivedAt;

        return (
          <SegmentGroup
            group={segmentGroup}
            inlineStyles={inlineStyles}
            key={`${segmentGroup.carrierBookingNumber.bookingNumber}-${segmentGroup.origin.cityCode}-${index}`}
            previousGroupArrival={previousGroupArrival}
          />
        );
      })}
    </div>
  );
};

Bound.propTypes = {
  bound: BoundNew,
};

export default Bound;
